import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiAlertCircle, FiSearch, FiTrendingUp, FiList, FiUpload, FiChevronLeft, FiChevronRight, FiEdit3, FiShoppingCart, FiZap, FiCheck, FiPieChart, FiDollarSign, FiPackage, FiX, FiCopy, FiPrinter } from 'react-icons/fi';
import ProductModal from '../components/ProductModal';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import Spinner from '../components/Spinner';
import AuthContext from '../context/AuthContext';
import ImportProductsModal from '../components/ImportProductsModal';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ProductHistoryModal from '../components/ProductHistoryModal'; // Importer la nouvelle modale
import ConfirmModal from '../components/ConfirmModal';
import JsBarcode from 'jsbarcode';

const AdjustStockModal = ({ isOpen, onClose, onSave, product }) => {
  const [newQuantity, setNewQuantity] = useState('');
  const [reason, setReason] = useState('Inventaire');

  useEffect(() => {
    if (product) {
      setNewQuantity(product.quantity);
    }
  }, [product]);

  if (!isOpen) return null;

  const handleSave = () => {
    const quantity = parseInt(newQuantity, 10);
    if (!isNaN(quantity) && quantity >= 0) {
      onSave(product._id, quantity, reason);
      onClose();
    } else {
      toast.error("Veuillez entrer une quantité valide.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
        <h3 className="text-xl font-semibold mb-4">Ajuster le stock de "{product?.name}"</h3>
        <p className="text-sm text-gray-500 mb-4">Quantité actuelle : <span className="font-bold">{product?.quantity}</span></p>
        <input type="number" placeholder="Nouvelle quantité" value={newQuantity} onChange={(e) => setNewQuantity(e.target.value)} className="w-full p-2 border rounded mb-4" autoFocus />
        <input type="text" placeholder="Raison (ex: Inventaire, Perte...)" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full p-2 border rounded mb-4" />
        <div className="flex justify-end space-x-3"><button onClick={onClose} className="bg-gray-200 text-gray-800 font-bold py-2 px-4 rounded-lg">Annuler</button><button onClick={handleSave} className="bg-brand-blue text-white font-bold py-2 px-4 rounded-lg">Enregistrer</button></div>
      </div>
    </div>
  );
};

const SmartRestockModal = ({ isOpen, onClose, products }) => {
  const restockSuggestions = useMemo(() => {
    return products
      .filter(p => !p.isService && p.quantity <= (p.lowStockThreshold || 5))
      .map(p => ({
        ...p,
        suggestedQty: (p.lowStockThreshold || 5) * 3 - p.quantity // Suggère de remonter à 3x le seuil
      }));
  }, [products]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-white">
          <h3 className="text-xl font-bold text-gray-800 flex items-center"><FiZap className="text-yellow-500 mr-2"/> Assistant Réapprovisionnement</h3>
          <p className="text-sm text-gray-500 mt-1">Basé sur vos seuils d'alerte, voici ce que vous devriez commander.</p>
        </div>
        <div className="p-6 overflow-y-auto">
          {restockSuggestions.length > 0 ? (
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-500 uppercase bg-gray-50"><tr><th className="p-3">Produit</th><th className="p-3">Stock Actuel</th><th className="p-3">Suggestion</th></tr></thead>
              <tbody>
                {restockSuggestions.map(p => (
                  <tr key={p._id} className="border-b"><td className="p-3 font-medium">{p.name}</td><td className="p-3 text-red-600 font-bold">{p.quantity}</td><td className="p-3 text-green-600 font-bold">+{p.suggestedQty}</td></tr>
                ))}
              </tbody>
            </table>
          ) : <div className="text-center py-8 text-gray-500"><FiCheck className="mx-auto h-12 w-12 text-green-200 mb-2"/>Votre stock est optimal ! Rien à commander.</div>}
        </div>
        <div className="p-4 border-t flex justify-end"><button onClick={onClose} className="bg-gray-900 text-white font-bold py-2 px-6 rounded-lg">Fermer</button></div>
      </div>
    </div>
  );
};

const MarginSimulatorModal = ({ isOpen, onClose }) => {
  const [purchasePrice, setPurchasePrice] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [margin, setMargin] = useState('');

  useEffect(() => {
    if (isOpen) {
        setPurchasePrice('');
        setSalePrice('');
        setMargin('');
    }
  }, [isOpen]);

  const calculateFromMargin = (m) => {
    setMargin(m);
    if (purchasePrice && m) {
        const price = parseFloat(purchasePrice) * (1 + parseFloat(m) / 100);
        setSalePrice(Math.round(price));
    }
  };

  const calculateFromPrice = (p) => {
    setSalePrice(p);
    if (purchasePrice && p) {
        const m = ((parseFloat(p) - parseFloat(purchasePrice)) / parseFloat(purchasePrice)) * 100;
        setMargin(m.toFixed(1));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-gray-800 flex items-center"><FiTrendingUp className="mr-2 text-green-500"/> Simulateur de Prix</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FiX size={24}/></button>
        </div>
        <div className="space-y-4">
            <div><label className="block text-sm font-bold text-gray-700 mb-1">Prix d'achat (Coût)</label><input type="number" value={purchasePrice} onChange={(e) => {setPurchasePrice(e.target.value); setSalePrice(''); setMargin('');}} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-brand-blue outline-none" placeholder="Ex: 1000" /></div>
            <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-bold text-gray-700 mb-1">Marge (%)</label><input type="number" value={margin} onChange={(e) => calculateFromMargin(e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none" placeholder="Ex: 20" /></div>
                <div><label className="block text-sm font-bold text-gray-700 mb-1">Prix Vente</label><input type="number" value={salePrice} onChange={(e) => calculateFromPrice(e.target.value)} className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 outline-none font-bold text-green-700" placeholder="Ex: 1200" /></div>
            </div>
            {purchasePrice && salePrice && (
                <div className="bg-gray-50 p-3 rounded-lg text-sm"><div className="flex justify-between mb-1"><span>Bénéfice unitaire :</span><span className="font-bold text-green-600">{(salePrice - purchasePrice).toLocaleString()} F</span></div><div className="flex justify-between"><span>Coefficient :</span><span className="font-bold">x{(salePrice / purchasePrice).toFixed(2)}</span></div></div>
            )}
        </div>
      </div>
    </div>
  );
};

const PrintLabelModal = ({ isOpen, onClose, onPrint, product }) => {
  const [quantity, setQuantity] = useState(1);
  const [format, setFormat] = useState('a4');

  useEffect(() => {
    if (isOpen && product) {
        setQuantity(product.quantity > 0 ? product.quantity : 1);
        setFormat('a4');
    }
  }, [isOpen, product]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
        <h3 className="text-xl font-semibold mb-4">Imprimer étiquettes</h3>
        <p className="text-sm text-gray-500 mb-4">Produit : <span className="font-bold">{product?.name}</span></p>
        
        <div className="space-y-4 mb-6">
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Nombre d'étiquettes</label>
                <input 
                    type="number" 
                    min="1"
                    value={quantity} 
                    onChange={(e) => setQuantity(parseInt(e.target.value) || "")} 
                    className="w-full p-2 border rounded text-lg font-bold text-center" 
                    autoFocus 
                />
            </div>
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Format du papier</label>
                <select 
                    value={format} 
                    onChange={(e) => setFormat(e.target.value)} 
                    className="w-full p-2 border rounded bg-white text-sm"
                >
                    <option value="a4">Planche A4 (Standard)</option>
                    <option value="thermal">Rouleau (Imprimante Thermique)</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">
                    {format === 'a4' ? "3 colonnes sur feuille A4." : "Une étiquette par page (50x30mm)."}
                </p>
            </div>
        </div>
        
        <div className="flex justify-end space-x-3">
            <button onClick={onClose} className="bg-gray-200 text-gray-800 font-bold py-2 px-4 rounded-lg">Annuler</button>
            <button onClick={() => onPrint(product, quantity, format)} disabled={!quantity || quantity < 1} className="bg-brand-blue text-white font-bold py-2 px-4 rounded-lg flex items-center disabled:opacity-50">
                <FiPrinter className="mr-2"/> Imprimer
            </button>
        </div>
      </div>
    </div>
  );
};

const getProductColor = (name) => {
  const colors = ['bg-red-100 text-red-600', 'bg-orange-100 text-orange-600', 'bg-amber-100 text-amber-600', 'bg-yellow-100 text-yellow-600', 'bg-lime-100 text-lime-600', 'bg-green-100 text-green-600', 'bg-emerald-100 text-emerald-600', 'bg-teal-100 text-teal-600', 'bg-cyan-100 text-cyan-600', 'bg-sky-100 text-sky-600', 'bg-blue-100 text-blue-600', 'bg-indigo-100 text-indigo-600', 'bg-violet-100 text-violet-600', 'bg-purple-100 text-purple-600', 'bg-fuchsia-100 text-fuchsia-600', 'bg-pink-100 text-pink-600', 'bg-rose-100 text-rose-600'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const ITEMS_PER_PAGE = 20;

const Stock = () => {
  const [allProducts, setAllProducts] = useState([]); // Cache pour tous les produits
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter');
  const [currentPage, setCurrentPage] = useState(1);
  const tableContainerRef = useRef(null);
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [adjustingProduct, setAdjustingProduct] = useState(null);
  const [isPrintLabelModalOpen, setIsPrintLabelModalOpen] = useState(false);
  const [productToPrint, setProductToPrint] = useState(null);

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedProductForHistory, setSelectedProductForHistory] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isRestockModalOpen, setIsRestockModalOpen] = useState(false);
  const [isSimulatorOpen, setIsSimulatorOpen] = useState(false);

  const { user, openUpgradeModal } = useContext(AuthContext);
  const isPremium = user?.plan === 'premium';

  // Fonction partagée pour générer l'image du code-barres
  const generateBarcodeImg = (text) => {
    try {
      const canvas = document.createElement("canvas");
      JsBarcode(canvas, text, {
        format: "CODE128",
        displayValue: true, // Affiche le code texte sous les barres
        fontSize: 16,
        textMargin: 0,
        margin: 0,
        width: 3,
        height: 60
      });
      return canvas.toDataURL("image/png");
    } catch (err) {
      console.error("Erreur génération code-barres", err);
      return null;
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        // On charge tous les produits en une seule fois pour le cache de recherche.
        // La pagination gérera l'affichage.
        const allProductsResponse = await api.get('/products');
        // On recalcule la quantité totale pour les produits avec variantes pour refléter les ventes
        const processedProducts = allProductsResponse.data.map(p => {
          if (p.variants && p.variants.length > 0) {
            const totalVariantQty = p.variants.reduce((sum, v) => sum + (parseInt(v.quantity, 10) || 0), 0);
            return { ...p, quantity: totalVariantQty };
          }
          return p;
        });
        setAllProducts(processedProducts);

      } catch (error) {
        console.error("Erreur lors de la récupération des produits:", error);
        toast.error("Impossible de charger les produits.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Réinitialiser à la première page si les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, initialFilter]);

  const refreshProducts = async () => {
    try {
      const response = await api.get('/products');
      // On recalcule aussi ici pour être cohérent après une modification
      const processedProducts = response.data.map(p => {
        if (p.variants && p.variants.length > 0) {
          const totalVariantQty = p.variants.reduce((sum, v) => sum + (parseInt(v.quantity, 10) || 0), 0);
          return { ...p, quantity: totalVariantQty };
        }
        return p;
      });
      setAllProducts(processedProducts);
    } catch (error) {
      console.error("Erreur lors de la récupération des produits:", error);
      toast.error("Impossible de charger les produits.");
    }
  };

  const handleImportProducts = async (productsToImport) => {
    const limit = user?.plan === 'premium' ? Infinity : user?.plan === 'pro' ? 250 : 25;
    if (allProducts.length + productsToImport.length > limit) {
      toast.info(`Cet import dépasserait votre limite de ${limit} produits. Passez au plan supérieur pour un stock illimité !`, { icon: "📦" });
      if (openUpgradeModal) openUpgradeModal();
      setIsImportModalOpen(false);
      return;
    }
    setLoading(true);
    try {
      // On suppose un nouvel endpoint pour l'import en masse
      const response = await api.post('/products/bulk', { products: productsToImport });
      toast.success(`${response.data.createdCount || 'Les'} produits ont été importés avec succès !`);
      await refreshProducts(); // Recharger la liste et attendre que ce soit fait
    } catch (error) {
      console.error("Erreur lors de l'importation des produits:", error);
      toast.error(error.response?.data?.message || "Une erreur est survenue lors de l'importation.");
    } finally {
      setLoading(false); // S'assurer que le chargement est terminé, même en cas d'erreur
      setIsImportModalOpen(false);
    }
  };

  const handleGenerateShoppingList = () => {
    if (user?.plan === 'essentiel') {
      toast.info("Générez automatiquement votre liste de courses. Passez au plan Pro pour ne plus jamais oublier un produit !", { icon: "🛒" });
      openUpgradeModal();
      return;
    }
    const lowStockItems = allProducts.filter(p => p.quantity <= (p.lowStockThreshold || 5));
    
    if (lowStockItems.length === 0) {
        toast.success("Tout va bien ! Aucun produit en rupture de stock.");
        return;
    }

    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text(`Liste de Courses - ${user?.shopName || 'Ma Boutique'}`, 14, 22);
    doc.setFontSize(11);
    doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 14, 30);

    const tableData = lowStockItems.map(p => [
        p.name,
        p.quantity,
        (p.lowStockThreshold || 5),
        "_______" // Colonne vide pour cocher
    ]);

    autoTable(doc, {
        startY: 40,
        head: [['Produit', 'En Stock', 'Seuil', 'Acheté']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: '#2196F3' }
    });

    doc.save('liste_courses_jaapande.pdf');
    toast.success("Liste de courses téléchargée !");
  };

  const openPrintLabelModal = (product) => {
    setProductToPrint(product);
    setIsPrintLabelModalOpen(true);
  };

  const handlePrintProductLabels = async (product, quantity, format = 'a4') => {
    if (!product || quantity <= 0) return;

    let barcodeValue = product.barcode;

    // Si pas de code, on le génère et on le sauvegarde définitivement
    if (!barcodeValue || barcodeValue.length === 0) {
        const generatedCode = `200${Math.floor(100000000 + Math.random() * 900000000)}`;
        try {
            await api.put(`/products/${product._id}`, { barcode: generatedCode });
            setAllProducts(prev => prev.map(p => p._id === product._id ? { ...p, barcode: generatedCode } : p));
            barcodeValue = generatedCode;
            toast.success("Code-barres généré et enregistré sur la fiche produit.");
        } catch (error) {
            console.error("Erreur sauvegarde barcode", error);
            barcodeValue = generatedCode; // On utilise quand même le code pour l'impression
        }
    }

    const barcodeImg = generateBarcodeImg(barcodeValue);

    if (format === 'thermal') {
        // Mode Rouleau / Thermique (50x30mm)
        const labelWidth = 50; 
        const labelHeight = 30;
        
        const doc = new jsPDF({
            orientation: 'landscape', 
            unit: 'mm',
            format: [labelHeight, labelWidth]
        });

        for (let i = 0; i < quantity; i++) {
            if (i > 0) doc.addPage([labelHeight, labelWidth], 'landscape');
            
            const centerX = labelWidth / 2;
            
            // Nom produit
            doc.setFontSize(9);
            doc.setFont("helvetica", "bold");
            // Tronquer si trop long (2 lignes max)
            const splitName = doc.splitTextToSize(product.name, labelWidth - 2);
            const nameLines = splitName.length > 2 ? splitName.slice(0, 2) : splitName;
            doc.text(nameLines, centerX, 3, { align: "center", baseline: "top" });
            
            // Prix
            // Calculer la position Y du prix en fonction de la taille du nom
            const priceY = 3 + (nameLines.length * 4) + 1;
            
            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            const formattedPrice = product.salePrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
            doc.text(`${formattedPrice} F`, centerX, priceY, { align: "center", baseline: "top" });
            
            // Image Code barre
            if (barcodeImg) {
                const imgW = 42;
                const imgH = 13;
                // Positionner en bas
                const imgY = labelHeight - imgH - 1; 
                doc.addImage(barcodeImg, 'PNG', (labelWidth - imgW) / 2, imgY, imgW, imgH);
            }
        }
        
        doc.save(`etiquettes_rouleau_${product.name.replace(/\s+/g, '_')}.pdf`);
    } else {
        // Mode A4
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        let x = 10;
        let y = 10;
        const labelWidth = 60;
        const labelHeight = 35;
        const margin = 5;

        for (let i = 0; i < quantity; i++) {
            // Contour pointillé pour découpe facile
            doc.setDrawColor(200);
            doc.setLineDash([2, 2], 0);
            doc.rect(x, y, labelWidth, labelHeight);
            doc.setLineDash([]);
            doc.setDrawColor(0);
            
            const centerX = x + labelWidth / 2;

            // Nom
            doc.setFontSize(9);
            doc.setFont("helvetica", "bold");
            const splitName = doc.splitTextToSize(product.name, labelWidth - 4);
            // Max 2 lignes
            const nameLines = splitName.length > 2 ? splitName.slice(0, 2) : splitName;
            doc.text(nameLines, centerX, y + 5, { align: "center", baseline: "top" });

            // Prix
            const priceY = y + 5 + (nameLines.length * 4) + 2;
            doc.setFontSize(18);
            doc.setFont("helvetica", "bold");
            const formattedPrice = product.salePrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
            doc.text(`${formattedPrice} F`, centerX, priceY, { align: "center", baseline: "top" });

            // Image Code barre
            if (barcodeImg) {
                const imgW = 50;
                const imgH = 15;
                const imgY = y + labelHeight - imgH - 2;
                doc.addImage(barcodeImg, 'PNG', x + (labelWidth - imgW) / 2, imgY, imgW, imgH);
            }

            x += labelWidth + margin;
            if (x + labelWidth > pageWidth) {
                x = 10;
                y += labelHeight + margin;
                if (y + labelHeight > pageHeight) {
                    doc.addPage();
                    y = 10;
                }
            }
        }
        doc.save(`etiquettes_${product.name.replace(/\s+/g, '_')}.pdf`);
    }

    toast.success(`${quantity} étiquettes générées !`);
    setIsPrintLabelModalOpen(false);
  };

  const handleAdjustStock = async (productId, newQuantity, reason) => {
    setLoading(true);
    try {
      await api.post(`/products/${productId}/adjust-stock`, { newQuantity, reason });
      toast.success("Stock ajusté avec succès !");
      await refreshProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || "Erreur lors de l'ajustement du stock.");
    } finally {
      setLoading(false);
      setIsAdjustModalOpen(false);
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct._id}`, productData);
        toast.success(`Produit "${productData.name}" modifié avec succès !`);
      } else {
        await api.post('/products', productData);
        toast.success(`Produit "${productData.name}" ajouté avec succès !`);
      }
      refreshProducts();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde du produit:", error);
      toast.error("Une erreur est survenue lors de la sauvegarde.");
    }
    setEditingProduct(null);
    setIsModalOpen(false);
  };

  const handleDeleteRequest = (product) => {
    setProductToDelete(product);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;
    try {
      await api.delete(`/products/${productToDelete._id}`);
      toast.success("Produit supprimé avec succès !");
      refreshProducts(); // Recharger les produits
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Impossible de supprimer le produit.");
    } finally {
      setIsConfirmModalOpen(false);
      setProductToDelete(null);
    }
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const openHistoryModal = (product) => {
    setSelectedProductForHistory(product);
    setIsHistoryModalOpen(true);
  };

  const openAdjustModal = (product) => {
    setAdjustingProduct(product);
    setIsAdjustModalOpen(true);
  };

  const handleAddProductClick = () => {
    const limit = user?.plan === 'premium' ? Infinity : user?.plan === 'pro' ? 250 : 25;
    if (allProducts.length >= limit) {
      toast.info(`Vous avez atteint la limite de ${limit} produits. Passez au plan supérieur pour un stock illimité !`, { icon: "📦" });
      if (openUpgradeModal) openUpgradeModal();
      return;
    }
    setEditingProduct(null);
    setIsModalOpen(true);
  };

  const handleImportClick = () => {
    const limit = user?.plan === 'premium' ? Infinity : user?.plan === 'pro' ? 250 : 25;
    if (allProducts.length >= limit) {
      toast.info(`Vous avez atteint la limite de ${limit} produits. Passez au plan supérieur pour un stock illimité !`, { icon: "📦" });
      if (openUpgradeModal) openUpgradeModal();
      return;
    }
    setIsImportModalOpen(true);
  };

  const categories = useMemo(() => ['Tous', ...new Set(allProducts.map(p => p.category || 'Non classé'))], [allProducts]);

  const filteredProducts = useMemo(() => {
    return allProducts.filter(product => {
      const isLowStock = !product.isService && product.quantity <= (product.lowStockThreshold || 5);
      const matchesFilter = initialFilter !== 'low_stock' || isLowStock;
      const matchesCategory = selectedCategory === 'Tous' || product.category === selectedCategory;
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesFilter && matchesCategory && matchesSearch;
    });
  }, [allProducts, searchTerm, selectedCategory, initialFilter]);

  const pageCount = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    tableContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const stockStats = useMemo(() => {
    return allProducts.reduce((acc, product) => {
      acc.totalItems += product.quantity;
      acc.totalPurchaseValue += (product.purchasePrice || 0) * product.quantity;
      acc.totalSaleValue += (product.salePrice || 0) * product.quantity;
      return acc;
    }, { totalItems: 0, totalPurchaseValue: 0, totalSaleValue: 0 });
  }, [allProducts]);

  if (loading) {
    return <Spinner message="Chargement des produits..." />;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold text-gray-800 font-poppins">Stock</h2>
        <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2">
          <button onClick={handleImportClick} className="justify-center bg-white border border-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-xl flex items-center text-sm shadow-sm active:bg-gray-50 transition-colors" >
            <FiUpload className="mr-2" />
            Importer
          </button>
          <button onClick={handleGenerateShoppingList} className="justify-center bg-white border border-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-xl flex items-center text-sm shadow-sm active:bg-gray-50 transition-colors" title="Générer une liste des produits en rupture">
            <FiShoppingCart className="mr-2" />
            Liste
          </button>
          <button onClick={() => setIsRestockModalOpen(true)} className="justify-center bg-purple-50 text-purple-700 font-semibold py-2 px-4 rounded-xl flex items-center text-sm border border-purple-100 active:bg-purple-100 transition-colors" title="Assistant IA">
            <FiZap className="mr-2" />
            Assistant
          </button>
          <button onClick={() => setIsSimulatorOpen(true)} className="justify-center bg-blue-50 text-blue-700 font-semibold py-2 px-4 rounded-xl flex items-center text-sm border border-blue-100 active:bg-blue-100 transition-colors" title="Calculer mes marges">
            <FiTrendingUp className="mr-2" />
            Prix
          </button>
          <button id="tour-step-add-product" onClick={handleAddProductClick} className="hidden md:flex bg-brand-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded-xl items-center shadow-md shadow-green-500/20 transition-all" >
            <FiPlus className="mr-2" />
            Nouveau
          </button>
        </div>
      </div>

      {/* Valorisation du Stock */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white p-3 sm:p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left">
            <div className="p-2 sm:p-3 bg-blue-50 text-blue-600 rounded-full mb-2 sm:mb-0 sm:mr-4"><FiPackage size={20}/></div>
            <div>
                <p className="text-xs text-gray-500 font-medium uppercase">Volume</p>
                <p className="text-lg sm:text-xl font-bold text-gray-800">{stockStats.totalItems.toLocaleString()}</p>
            </div>
        </div>
        <div className="bg-white p-3 sm:p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left">
            <div className="p-2 sm:p-3 bg-green-50 text-green-600 rounded-full mb-2 sm:mb-0 sm:mr-4"><FiDollarSign size={20}/></div>
            <div>
                <p className="text-xs text-gray-500 font-medium uppercase">Valeur Vente</p>
                <p className="text-lg sm:text-xl font-bold text-gray-800">{stockStats.totalSaleValue.toLocaleString('fr-FR')} F</p>
                <p className="text-[10px] text-green-600 font-medium hidden sm:block">Marge: {(stockStats.totalSaleValue - stockStats.totalPurchaseValue).toLocaleString('fr-FR')} F</p>
            </div>
        </div>
        {/* Vous pouvez ajouter une 3ème carte ici si nécessaire */}
      </div>

      {/* Filtres et Recherche */}
      <div className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur-sm py-2 -mx-4 px-4 sm:static sm:bg-transparent sm:p-0 sm:mx-0 mb-6 transition-all">
        <div className="flex flex-col gap-3">
          <div className="relative w-full shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><FiSearch className="text-gray-400" /></div>
            <input 
              type="text" 
              placeholder="Rechercher un produit..." 
              className="block w-full pl-10 pr-3 py-3 border-none rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue sm:text-sm"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
            {categories.map(category => (
              <button 
                key={category} 
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all border ${selectedCategory === category ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Vue Mobile (Cartes) - App Style */}
      <div className="flex flex-col gap-3 md:hidden pb-20">
        {paginatedProducts.map((product) => {
           const isLowStock = !product.isService && product.quantity <= (product.lowStockThreshold || 5);
           const hasPurchasePrice = product.purchasePrice != null && product.purchasePrice > 0;
           const profitMargin = hasPurchasePrice ? product.salePrice - product.purchasePrice : null;
           
           return (
             <div key={product._id} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 active:scale-[0.98] transition-transform duration-200">
               <div className="flex gap-4">
                 {/* Avatar/Image */}
                 <div className={`w-14 h-14 rounded-xl flex-shrink-0 flex items-center justify-center text-xl font-bold ${getProductColor(product.name)}`}>
                    {product.name.substring(0, 2).toUpperCase()}
                 </div>
                 
                 {/* Content */}
                 <div className="flex-grow min-w-0">
                   <div className="flex justify-between items-start">
                     <h3 className="font-bold text-gray-900 truncate pr-2">{product.name}</h3>
                     <span className="font-bold text-gray-900 whitespace-nowrap">{product.salePrice.toLocaleString('fr-FR')} F</span>
                   </div>
                   
                   <div className="flex justify-between items-center mt-1">
                     <p className="text-xs text-gray-500 font-mono truncate">{product.barcode || 'Sans code'}</p>
                     <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${isLowStock ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                       {product.isService ? 'Service' : `Stock: ${product.quantity}`}
                     </span>
                   </div>

                   {/* Marge info (optional or small) */}
                   {profitMargin !== null && (
                      <p className="text-[10px] text-gray-400 mt-1">
                        Marge: <span className={profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}>{profitMargin.toLocaleString()} F</span>
                      </p>
                   )}
                 </div>
               </div>

               {/* Actions Bar */}
               <div className="grid grid-cols-4 gap-2 mt-4 pt-3 border-t border-gray-50">
                  <button onClick={() => openPrintLabelModal(product)} className="flex flex-col items-center justify-center text-gray-500 hover:text-gray-800 active:text-gray-800 py-1">
                    <FiPrinter size={18} />
                    <span className="text-[10px] mt-1">Étiquettes</span>
                  </button>
                  <button onClick={() => openAdjustModal(product)} className="flex flex-col items-center justify-center text-gray-500 hover:text-green-600 active:text-green-600 py-1">
                    <FiEdit3 size={18} />
                    <span className="text-[10px] mt-1">Ajuster</span>
                  </button>
                  <button onClick={() => openEditModal(product)} className="flex flex-col items-center justify-center text-gray-500 hover:text-blue-600 active:text-blue-600 py-1">
                    <FiEdit size={18} />
                    <span className="text-[10px] mt-1">Modifier</span>
                  </button>
                  <button onClick={() => openHistoryModal(product)} className="flex flex-col items-center justify-center text-gray-500 hover:text-purple-600 active:text-purple-600 py-1">
                    <FiList size={18} />
                    <span className="text-[10px] mt-1">Historique</span>
                  </button>
                  <button onClick={() => handleDeleteRequest(product)} className="flex flex-col items-center justify-center text-gray-500 hover:text-red-600 active:text-red-600 py-1">
                    <FiTrash2 size={18} />
                    <span className="text-[10px] mt-1">Supprimer</span>
                  </button>
               </div>
             </div>
           );
        })}
      </div>

      {/* Floating Action Button for Mobile */}
      <button 
        onClick={handleAddProductClick}
        className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-brand-green text-white rounded-full shadow-lg shadow-green-500/40 flex items-center justify-center z-40 active:scale-90 transition-transform"
      >
        <FiPlus size={28} />
      </button>

      <div ref={tableContainerRef} className="bg-white shadow-sm rounded-2xl overflow-hidden hidden md:block border border-gray-100">
        <div className="overflow-x-auto">
          <table className="min-w-full leading-normal">
            <thead className="sticky top-0 z-10">
              <tr className="border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <th className="px-5 py-3">Nom du produit</th>
                <th className="px-5 py-3">Code-barres</th>
                <th className="px-5 py-3">Catégorie</th>
                <th className="px-5 py-3">Prix d'achat</th>
                <th className="px-5 py-3">Prix de vente</th>
                <th className="px-5 py-3">Quantité</th>
                <th className="px-5 py-3">Marge Brute</th>
                {isPremium && <th className="px-5 py-3">Seuil</th>}
                <th className="px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProducts.map((product) => {
                const isLowStock = !product.isService && product.quantity <= (product.lowStockThreshold || 5);
                const hasPurchasePrice = product.purchasePrice != null && product.purchasePrice > 0;
                const profitMargin = hasPurchasePrice ? product.salePrice - product.purchasePrice : null;
                return (<tr key={product._id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="px-5 py-4 text-sm">
                    <div className="flex items-center">
                      <p className="text-gray-900 whitespace-no-wrap">{product.name}</p>                      
                      {!hasPurchasePrice && (
                        <FiAlertCircle 
                          className="text-yellow-500 ml-2" 
                          title="Prix d'achat manquant. Les calculs de bénéfice pour ce produit seront incorrects." 
                        />
                      )}
                      {isLowStock && <FiAlertCircle className="text-red-500 ml-2" title="Stock faible" />}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <p className="text-gray-600 whitespace-no-wrap font-mono">{product.barcode || '-'}</p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <p className="text-gray-900 whitespace-no-wrap">{product.category}</p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    {hasPurchasePrice ? (
                      <p className="text-gray-900 whitespace-no-wrap">{product.purchasePrice.toLocaleString('fr-FR')} CFA</p>
                    ) : (
                      <span className="text-gray-400 italic">N/A</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <p className="text-gray-900 whitespace-no-wrap font-semibold">{product.salePrice.toLocaleString('fr-FR')} CFA</p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    {product.isService ? (
                        <span className="relative inline-block px-3 py-1 font-semibold leading-tight text-blue-900"><span aria-hidden className="absolute inset-0 bg-blue-200 opacity-50 rounded-full"></span><span className="relative">Service</span></span>
                    ) : (
                        <span className={`relative inline-block px-3 py-1 font-semibold leading-tight ${
                        isLowStock ? 'text-red-900' : 'text-green-900'
                        }`}>
                        <span aria-hidden className={`absolute inset-0 ${isLowStock ? 'bg-red-200' : 'bg-green-200'} opacity-50 rounded-full`}></span>
                        <span className="relative">{product.quantity}</span>
                        </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm">
                    {profitMargin !== null ? (
                      <div className={`font-semibold ${profitMargin >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {profitMargin.toLocaleString('fr-FR')} CFA
                        {profitMargin > (product.purchasePrice * 0.5) && <FiTrendingUp className="inline ml-1 text-green-500" title="Marge élevée" />}
                      </div>
                    ) : (
                      <span className="text-gray-400 italic">N/A</span>
                    )}
                  </td>
                  {isPremium && <td className="px-5 py-4 text-sm text-center">
                    <span className="text-gray-600">{product.isService ? '-' : (product.lowStockThreshold || 5)}</span>
                  </td>}
                  <td className="px-5 py-4 text-sm">
                    <div className="flex items-center space-x-3">
                      {!product.isService && <button onClick={() => openAdjustModal(product)} className="text-gray-500 hover:text-green-500" title="Ajustement rapide du stock"><FiEdit3 className="w-5 h-5" /></button>}
                      {!product.isService && <button onClick={() => openPrintLabelModal(product)} className="text-gray-500 hover:text-gray-800" title="Imprimer des étiquettes"><FiPrinter className="w-5 h-5" /></button>}
                      <button onClick={() => openEditModal(product)} className="text-gray-500 hover:text-brand-blue" title="Modifier le produit"><FiEdit className="w-5 h-5" /></button>
                      <button onClick={() => openHistoryModal(product)} className="text-gray-500 hover:text-purple-500" title="Voir l'historique du produit"><FiList className="w-5 h-5" /></button>
                      <button onClick={() => handleDeleteRequest(product)} className="text-gray-500 hover:text-red-500"><FiTrash2 className="w-5 h-5" /></button>
                    </div>
                  </td>
                </tr>)
              })}
            </tbody>
          </table>
        </div>
        {pageCount > 1 && (
          <div className="px-5 py-5 bg-white border-t flex flex-col xs:flex-row items-center xs:justify-between">
            <span className="text-xs xs:text-sm text-gray-900">
              Affichage de {paginatedProducts.length} sur {filteredProducts.length} produits
            </span>
            <div className="inline-flex mt-2 xs:mt-0">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="text-sm bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-l disabled:opacity-50">
                  <FiChevronLeft/>
              </button>
              <button
                onClick={() => handlePageChange(Math.min(pageCount, currentPage + 1))}
                disabled={currentPage === pageCount}
                className="text-sm bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-r disabled:opacity-50">
                  <FiChevronRight/>
              </button>
            </div>
          </div>
        )}
      </div>
      <ProductModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSave={handleSaveProduct} 
        product={editingProduct} 
        categories={categories.filter(c => c !== 'Tous' && c !== 'Non classé')} />

      <ImportProductsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImportProducts}
      />

      <AdjustStockModal
        isOpen={isAdjustModalOpen}
        onClose={() => setIsAdjustModalOpen(false)}
        onSave={handleAdjustStock}
        product={adjustingProduct}
      />
      
      <SmartRestockModal 
        isOpen={isRestockModalOpen}
        onClose={() => setIsRestockModalOpen(false)}
        products={allProducts}
      />

      <MarginSimulatorModal 
        isOpen={isSimulatorOpen}
        onClose={() => setIsSimulatorOpen(false)}
      />

      <ProductHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        productId={selectedProductForHistory?._id}
        productName={selectedProductForHistory?.name}
        currentProduct={selectedProductForHistory}
      />

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Supprimer le produit"
        message={`Êtes-vous sûr de vouloir supprimer "${productToDelete?.name}" ? Cette action est irréversible.`}
      />

      <PrintLabelModal 
        isOpen={isPrintLabelModalOpen} 
        onClose={() => setIsPrintLabelModalOpen(false)} 
        onPrint={handlePrintProductLabels} 
        product={productToPrint} 
      />
    </div>
  );
};

export default Stock;